// src/hooks/useLeadSupabaseSync.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Lead } from "@/types/lead";
import { originLocationKey, stampLeadOriginIfBlank } from "@/lib/lead/locationScope";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { afterSuccessfulLeadUpsert } from "@/lib/leadAchievementCheckAfterUpsert";

const UPSERT_DEBOUNCE_MS = 1500;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const hasUuidId = (l: Lead) => UUID_RE.test(l.id);
const toUuidOrNull = (v: string | null | undefined): string | null =>
  v && UUID_RE.test(v) ? v : null;

function mergeLeads(local: Lead[], remote: Lead[]): Lead[] {
  const map = new Map<string, Lead>();
  for (const lead of local) map.set(lead.id, lead);
  for (const lead of remote) {
    const existing = map.get(lead.id);
    if (!existing || lead.lastUpdated > existing.lastUpdated) {
      map.set(lead.id, lead);
    }
  }
  return Array.from(map.values());
}

/**
 * A lead is "viable" for Supabase sync if extracted data contains:
 *  - a full name (first_name AND last_name, both non-empty)
 *  - at least one of: phone or email
 *  - a product_line
 */
function isViableLead(l: Lead): boolean {
  const e = l.extracted;
  if (!e) return false;
  const hasName = !!e.first_name?.trim() && !!e.last_name?.trim();
  const hasContact = !!e.phone?.trim() || !!e.email?.trim();
  const hasProduct = !!e.product_line;
  return hasName && hasContact && hasProduct;
}

interface UseLeadSupabaseSyncArgs {
  leads: Lead[];
  setLeads: (leads: Lead[]) => void;
  userId: string | null | undefined;
  organizationId: string | null | undefined;
  locationId: string | null | undefined;
  locationName: string | null | undefined;
  /** When true (trainer/admin), load all org draft/sent leads instead of owner-only. */
  viewOrgWide?: boolean;
}

function hydrateLeadFromRow(row: {
  id: string;
  data: Lead | null;
  location_id: string | null;
  owner_user_id?: string | null;
  created_at?: string | null;
}): Lead | null {
  const raw = row.data;
  if (!raw) return null;
  const lid = row.location_id;
  const key = originLocationKey(raw, lid);
  return {
    ...raw,
    origin_location_id: (raw.origin_location_id || "").trim() || key || null,
    origin_location_name: raw.origin_location_name ?? null,
    owner_user_id: row.owner_user_id ?? raw.owner_user_id ?? null,
    created_at: row.created_at ?? raw.created_at ?? null,
  };
}

function prepareRowForUpsert(
  l: Lead,
  organizationId: string,
  userId: string,
  workspaceLocationId: string | null | undefined,
  workspaceLocationName: string | null | undefined
): { row: Record<string, unknown>; leadPayload: Lead } {
  const payload = stampLeadOriginIfBlank(l, workspaceLocationId, workspaceLocationName);
  const origin = (payload.origin_location_id || "").trim();
  const rowLocationId = toUuidOrNull(origin);
  return {
    leadPayload: payload,
    row: {
      id: payload.id,
      organization_id: organizationId,
      location_id: rowLocationId,
      owner_user_id: userId,
      status: payload.sent ? "sent" : "draft",
      data: payload,
      is_test: payload.isTest ?? false,
      sent_at: payload.sent ? new Date(payload.lastUpdated).toISOString() : null,
    },
  };
}

export function useLeadSupabaseSync({
  leads,
  setLeads,
  userId,
  organizationId,
  locationId,
  locationName,
  viewOrgWide = false,
}: UseLeadSupabaseSyncArgs) {
  const fresh = useFreshAchievementsOptional();
  const leadsRef = useRef(leads);
  leadsRef.current = leads;
  const viableRef = useRef<Map<string, boolean>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** IDs archived this session — excluded from upserts */
  const archivedIdsRef = useRef<Set<string>>(new Set());
  /** IDs known to exist in Supabase as active (draft/sent) rows */
  const [syncedIds, setSyncedIds] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  /** True after the initial Supabase load attempt completes (success or error). */
  const [isInitialSyncComplete, setIsInitialSyncComplete] = useState(false);
  const syncedIdsRef = useRef<Set<string>>(new Set());
  syncedIdsRef.current = syncedIds;

  useEffect(() => {
    const next = new Map<string, boolean>();
    for (const l of leads) next.set(l.id, isViableLead(l));
    viableRef.current = next;
  }, [leads]);

  // ── Initial load (re-runs when workspace org or user changes) ───────────────
  // Fetch draft/sent leads for this org only, drop UUID leads from other orgs from
  // local state, keep local-only (non-UUID) drafts, then flush viable locals.
  useEffect(() => {
    if (!userId || !organizationId || !supabase) {
      setIsInitialSyncComplete(true);
      return;
    }

    setIsInitialSyncComplete(false);
    let cancelled = false;
    const db = supabase;

    (async () => {
      let query = db
        .from("leads")
        .select("id, data, location_id, owner_user_id, created_at")
        .eq("organization_id", organizationId)
        .in("status", ["draft", "sent"])
        .order("updated_at", { ascending: false });
      if (!viewOrgWide) {
        query = query.eq("owner_user_id", userId);
      }

      const { data, error } = await query;

      if (cancelled) return;

      if (error) {
        console.warn("[supasync] load error", error.message);
        setIsInitialSyncComplete(true);
        return;
      }

      const remoteIds = new Set((data ?? []).map((row) => row.id as string));
      const remote: Lead[] = (data ?? [])
        .map((row) =>
          hydrateLeadFromRow({
            id: row.id as string,
            data: row.data as Lead | null,
            location_id: (row.location_id as string | null) ?? null,
            owner_user_id: (row.owner_user_id as string | null) ?? null,
            created_at: (row.created_at as string | null) ?? null,
          })
        )
        .filter((x): x is Lead => Boolean(x));

      setSyncedIds(remoteIds);

      const mergedForFlush = mergeLeads(leadsRef.current.filter((l) => !hasUuidId(l)), remote);
      setLeads(mergedForFlush);

      const toFlush = mergedForFlush.filter(
        (l) =>
          hasUuidId(l) &&
          !remoteIds.has(l.id) &&
          !archivedIdsRef.current.has(l.id) &&
          isViableLead(l)
      );
      if (toFlush.length > 0) {
        const rows = toFlush.map((l) => prepareRowForUpsert(l, organizationId, userId, locationId, locationName).row);
        const prevSynced = syncedIdsRef.current;
        const prevViable = new Map(viableRef.current);
        const { error: flushErr } = await db.from("leads").upsert(rows, { onConflict: "id" });
        if (!flushErr) {
          setSyncedIds((prev) => new Set([...prev, ...toFlush.map((l) => l.id)]));
          afterSuccessfulLeadUpsert(prevSynced, rows, mergedForFlush, prevViable, fresh);
        } else {
          console.warn("[supasync] onload flush error", flushErr.message);
        }
      }
      if (!cancelled) setIsInitialSyncComplete(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, organizationId, viewOrgWide]);

  // ── Debounced upsert on leads state change ────────────────────────────────────
  useEffect(() => {
    if (!userId || !organizationId || !supabase || !isInitialSyncComplete) return;
    const db = supabase;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const toUpsert = leads
        .filter((l) => hasUuidId(l) && !archivedIdsRef.current.has(l.id))
        .map((l) => prepareRowForUpsert(l, organizationId, userId, locationId, locationName).row);

      if (toUpsert.length === 0) return;

      const prevSynced = syncedIdsRef.current;
      const prevViable = new Map(viableRef.current);
      const { error } = await db
        .from("leads")
        .upsert(toUpsert, { onConflict: "id" });

      if (!error) {
        setSyncedIds((prev) => {
          const next = new Set(prev);
          for (const u of toUpsert) next.add(String(u.id));
          return next;
        });
        afterSuccessfulLeadUpsert(prevSynced, toUpsert, leadsRef.current, prevViable, fresh);
      } else {
        console.warn("[supasync] upsert error", error.message);
      }
    }, UPSERT_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
     
  }, [leads, userId, organizationId, locationId, locationName, isInitialSyncComplete, fresh]);

  // ── Manual sync-all ───────────────────────────────────────────────────────────
  // Upserts viable leads not yet confirmed in syncedIds. Safe to call repeatedly.
  const syncAllLeads = useCallback(async () => {
    if (!userId || !organizationId || !supabase) return;
    const db = supabase;

    setIsSyncing(true);
    try {
      const toSync = leads.filter(
        (l) =>
          hasUuidId(l) &&
          !archivedIdsRef.current.has(l.id) &&
          !syncedIds.has(l.id) &&
          isViableLead(l)
      );
      if (toSync.length === 0) return;

      const rows = toSync.map((l) =>
        prepareRowForUpsert(l, organizationId, userId, locationId, locationName).row
      );

      const prevSynced = syncedIdsRef.current;
      const prevViable = new Map(viableRef.current);
      const { error } = await db.from("leads").upsert(rows, { onConflict: "id" });
      if (error) {
        console.warn("[supasync] syncAll error", error.message);
        return;
      }
      setSyncedIds((prev) => new Set([...prev, ...toSync.map((l) => l.id)]));
      afterSuccessfulLeadUpsert(prevSynced, rows, leads, prevViable, fresh);
    } finally {
      setIsSyncing(false);
    }
  }, [leads, syncedIds, userId, organizationId, locationId, locationName, fresh]);

  // ── Purge (hard-delete truly empty placeholder leads) ────────────────────────
  // Adds IDs to archivedIdsRef immediately so the debounced upsert skips them,
  // then fires a Supabase hard-delete. Safe to call for IDs that never reached
  // Supabase (delete-where-not-exists is a no-op).
  const purgeLeads = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    for (const id of ids) archivedIdsRef.current.add(id);
    setSyncedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    const db = supabase;
    if (!db) return;
    const { error } = await db.from("leads").delete().in("id", ids);
    if (error) console.warn("[supasync] purge error", error.message);
  }, []);

  // ── Archive (strip data after GHL send) ──────────────────────────────────────
  const archiveLead = useCallback(async (leadId: string) => {
    const db = supabase;
    if (!db) return;
    const { error } = await db.rpc("archive_lead", { p_lead_id: leadId });
    if (error) {
      console.warn("[supasync] archive error", leadId, error.message);
      return;
    }
    archivedIdsRef.current.add(leadId);
    // Remove from syncedIds — data is gone from Supabase
    setSyncedIds((prev) => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
  }, []);

  // ── Derived counts ────────────────────────────────────────────────────────────
  const syncedCount = useMemo(
    () => leads.filter((l) => syncedIds.has(l.id)).length,
    [leads, syncedIds]
  );

  const viableCount = useMemo(
    () =>
      leads.filter(
        (l) => !archivedIdsRef.current.has(l.id) && isViableLead(l)
      ).length,
    // archivedIdsRef is a ref; viable leads change whenever leads changes
     
    [leads]
  );

  return { archiveLead, purgeLeads, syncAllLeads, syncedCount, viableCount, isSyncing, isInitialSyncComplete };
}
